interface InsurancePlanDetails {
  va?: boolean;
  hmo?: boolean;
  medicareAdvantage?: boolean;
  commercial?: boolean;
  primaryPlan?: boolean;
  secondaryPlan?: boolean;
  marketplace?: boolean;
  startDate?: string | null;
  endDate?: string | null;
}

interface StatusConfig {
  bgColor: string;
  label: string; // Badge title (1st line)
  helperText: string; // Badge subtitle (2nd line)
  priority: number; // For sorting/filtering
}

/**
 * Pulls the operator-authored `note` out of an insurance grid entry's
 * metadata. Anything that isn't a non-empty string is treated as absent.
 */
const getMetadataNote = (metadata: unknown): string | undefined => {
  if (!metadata || typeof metadata !== 'object') {
    return undefined;
  }

  const { note } = metadata as { note?: unknown };
  return typeof note === 'string' && note.trim() ? note.trim() : undefined;
};

export const getStatusConfig = (
  status: string | undefined | null,
  planDetails?: InsurancePlanDetails,
  taxId?: string,
  credentialStatus?: string | undefined | null,
  metadata?: unknown
): StatusConfig => {
  // Handle combined status approach - if we have credential status info, use it
  if (credentialStatus?.toLowerCase() === 'pending') {
    return {
      bgColor: 'yellow.500',
      label: 'Do not schedule',
      helperText: 'Credential status pending review',
      priority: 4,
    };
  }

  if (!status) {
    return {
      bgColor: 'orange.500',
      label: 'Status Unknown',
      helperText: 'Contact administrator for clarification',
      priority: 6,
    };
  }

  // Convert status to lowercase for comparison
  const statusLower = status.toLowerCase();

  let baseConfig: StatusConfig;

  switch (statusLower) {
    case 'schedule allowed':
      baseConfig = {
        bgColor: planDetails?.va ? 'teal.500' : 'green.500',
        label: planDetails?.va
          ? 'New patients MUST be 10+ days out'
          : 'OK to Schedule',
        helperText: planDetails?.hmo
          ? 'Assigned HMO PCP must be in the tax ID'
          : 'Provider credentialed and active',
        priority: 1,
      };
      break;

    case 'not participating':
      baseConfig = {
        bgColor: 'red.500',
        label: 'Do not schedule',
        helperText: 'Provider does not take this plan',
        priority: 5,
      };
      break;

    // Suspected dead: 'Pending Credentialing' not seen in production plan values — investigate before removing
    case 'pending credentialing':
      baseConfig = {
        bgColor: 'yellow.500',
        label: 'Do not schedule',
        helperText: 'Application/credentialing is in process',
        priority: 4,
      };
      break;

    // Suspected dead: 'Provider Does Not Take This Plan' not seen in production plan values — investigate before removing
    case 'provider does not take this plan':
      baseConfig = {
        bgColor: 'red.500',
        label: 'Do not schedule',
        helperText: 'Provider does not take this plan',
        priority: 5,
      };
      break;

    case 'established patients':
      baseConfig = {
        bgColor: 'blue.500',
        label: 'Schedule Established patients',
        helperText: 'Panel is closed; No new patients',
        priority: 2,
      };
      break;

    // Suspected dead: 'Panel Is Closed: No New Patients' not seen in production plan values — investigate before removing
    case 'panel is closed: no new patients':
      baseConfig = {
        bgColor: 'blue.500',
        label: 'Schedule Established patients',
        helperText: 'Panel is closed; No new patients',
        priority: 2,
      };
      break;

    case 'pcp match':
      baseConfig = {
        bgColor: 'purple.500',
        label: 'HMO plan requires exact PCP match',
        helperText: 'Exact PCP match required for HMO plans',
        priority: 3,
      };
      break;

    case 'no status':
      baseConfig = {
        bgColor: 'orange.500',
        label: 'Status Unknown',
        helperText: 'Contact administrator for clarification',
        priority: 6,
      };
      break;

    // Suspected dead: 'Credentialed' not seen in production plan values — investigate before removing
    case 'credentialed':
      baseConfig = {
        bgColor: 'green.500',
        label: 'OK to Schedule',
        helperText: 'Provider credentialed and active',
        priority: 1,
      };
      break;

    case 'pending':
      baseConfig = {
        bgColor: 'yellow.500',
        label: 'Do not schedule',
        helperText: 'Status pending review',
        priority: 4,
      };
      break;

    default:
      baseConfig = {
        bgColor: 'orange.500',
        label: status,
        helperText: 'Unknown status - contact administrator',
        priority: 6,
      };
  }

  // Green plans read their scripting off the grid entry's metadata note when
  // one is set, e.g. TRICARE plans say "Only as Secondary / Requires DBM
  // Number" instead of the generic "Provider credentialed and active".
  const note = getMetadataNote(metadata);
  if (note && baseConfig.bgColor === 'green.500') {
    baseConfig.helperText = note;
  }

  if (taxId) {
    baseConfig.helperText += ` | Tax ID: ${taxId}`;
  }

  return baseConfig;
};
